<template>
  <div class="sections-holder">
    <FaqSection>
      <span slot="heading">
        P-value vs rejection region in hypothesis testing
      </span>
      <div slot="content">
        <p>The p-value and the rejection region are two common methods used in hypothesis testing. Both aim to make a decision based on sample data, but they differ in how they interpret and calculate the evidence against the null hypothesis:</p>
        <ul>
          <li>The p-value indicates how strong the evidence against the null hypothesis is. A small p-value suggests that the observed data is implausible if the null hypothesis is true, which means we reject the latter in favor of the alternative hypothesis. In this approach, we have a predetermined significance level (α), typically 0.05. If the p-value is less than or equal to that, we reject the null hypothesis.</li>
          <li>Meanwhile, the rejection region involves determining a critical value (or cutoff value) based on a predetermined significance level (α). We then compare the test statistic (calculated from the sample data) with this critical value. If the test statistic is larger (for a one-tailed test), or falls outside the critical range (for a two-tailed test), then we reject the null hypothesis in favor of the alternative hypothesis. Unlike the p-value, the rejection region does not directly measure how strong the evidence against the null hypothesis is.</li>
        </ul>
        <p>In summary, the p-value approach quantifies the strength of evidence against the null hypothesis based on the probability of obtaining the observed test statistic. Meanwhile, the rejection region compares the test statistic to a critical value before reaching a conclusion. The p-value provides more flexibility and a continuous measure of evidence, whereas the rejection region simplifies the decision-making process by using predetermined critical values.</p>
        <img src="../../../../assets/images/calculators/hypothesis-test/hypothesis-test-16.webp" alt="Rejection and Non-rejection regions graph.">
        <p class="image-headings">
          Two Tail Hypothesis Testing
        </p>
        <img src="../../../../assets/images/calculators/hypothesis-test/hypothesis-test-17.webp" alt="Two tail hypothesis testing graph.">
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        What is a test statistic in hypothesis testing?
      </span>
      <div slot="content">
        <p>In hypothesis testing, a test statistic is a numerical value calculated from the sample data used to make inferences about the population parameter of interest. It serves as a basis for comparison between the observed data and the expectations we have under the assumption of the null hypothesis.</p>
        <p>The specific test statistic depends on the hypothesis at hand and the type of data we’re analyzing. For example, when the population variance is <span class="bolded-text">known</span> and the sample size is <span class="bolded-text">greater than 30</span>, then we use the <span class="bolded-text">z-statistic</span>. Alternatively, when the population variance is <span class="bolded-text">unknown</span> and the sample size is <span class="bolded-text">less than 30</span>, then we typically opt for the t-statistic.</p>
        <p>Once we calculate the test statistic, we compare it to a critical value to determine whether to reject or fail to reject the null hypothesis.</p>
        <p>The test statistic in hypothesis testing essentially summarizes the information in the sample data and provides a basis on which to make an objective decision about the null hypothesis.</p>
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        What is level of significance in hypothesis testing?
      </span>
      <div slot="content">
        <p>The level of significance in hypothesis testing is denoted as the Greek letter α and represents a pre-determined threshold that helps identify the criteria for rejecting or failing to reject the null hypothesis.</p>
        <p>The significance level also represents the probability of making a Type I error, which is when we reject the null hypothesis but it is actually true. Commonly used significance levels in hypothesis testing include 0.05 (5%) and 0.01 (1%). We can choose other levels such as 0.10 (10%) based on the specific context and requirements of the study.</p>
        <p>When conducting a hypothesis test, the researcher compares the test statistic (calculated from the sample data) to the critical value(s) or calculates the p-value. If the test statistic falls in the rejection region defined by the critical value(s)—or if the p-value is smaller than the significance level— then we reject the null hypothesis. Conversely, if the test statistic falls in the non-rejection region—or the p-value is larger than the significance level—we fail to reject the null hypothesis.</p>
        <p>Choosing a significance level is a balance between making accurate conclusions and controlling the risk of Type I errors. A lower significance level (e.g., α = 0.01) makes it more rigorous to reject the null hypothesis, reducing the chance of Type I errors, but potentially increasing the likelihood of Type II errors—failing to reject the null hypothesis when it’s false. That’s because we’ll reject H0 less frequently, including when it is false. On the other hand, a higher significance level (e.g., α = 0.10) increases the chances of Type I errors, but decreases the chances of Type II errors.</p>
        <p>Feel free to experiment with different significance levels for your sample in our Hypothesis Testing Calculator and optimize your analyses.</p>
      </div>
    </FaqSection>
  </div>
</template>

<script>
import FaqSection from './Section.vue';

export default {
  components: {
    FaqSection,
  },
};
</script>

<style lang="scss" scoped>
.sections-holder {
  hr {
    margin: var(--spacing-8) 0 var(--spacing-24);
    border: 1px solid rgba(0, 12, 31, 0.12);
  }

  .content {
    ol {
      padding-left: 23px;
    }

    img {
      margin-left: var(--spacing-50);
      margin-right: var(--spacing-50);
    }

    ol,
    ul {
      margin-top: var(--spacing-16);

      li {
        margin-top: var(--spacing-16);
      }
    }
  }
}

@include bp($bp-mobile) {
  .sections-holder {
    .content {
      img {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
}
</style>
